import React, { useMemo } from "react";
import { X as CloseIcon, Loader2, Globe2 } from "lucide-react";
import { FlowLogo, MercadoPagoLogo } from "./PaymentBrandLogos";

/**
 * Detects whether the visitor is browsing from Chile.
 * Uses the IANA timezone reported by the browser — no IP/geolocation API call needed.
 * Conservative: any non-Santiago timezone is treated as "outside Chile" so we
 * can show the "tarjetas extranjeras" hint to international users.
 */
const isVisitorInChile = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    return tz === "America/Santiago" || tz === "America/Punta_Arenas" || tz === "Pacific/Easter";
  } catch {
    return true; // fail-safe: assume CL (don't show the hint)
  }
};

/**
 * PaymentGatewayModal
 *
 * Asks the user to pick between Flow.cl and Mercado Pago for the
 * recurring "Plan Mensual" subscription.
 */
export default function PaymentGatewayModal({
  open,
  onClose,
  onSelect,
  loading = "",
  error = "",
  planLabel = "Plan Mensual — $9.990 CLP / mes",
}) {
  const showForeignHint = useMemo(() => !isVisitorInChile(), []);

  if (!open) return null;
  const disabled = !!loading;

  return (
    <div
      data-testid="payment-gateway-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.45)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
        padding: 20,
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 460,
          background: "var(--pl-card, #fff)",
          border: "1px solid var(--pl-border, #e5e7eb)",
          borderRadius: 18,
          padding: "28px 26px",
          color: "var(--pl-text, #0f172a)",
          boxShadow: "var(--pl-shadow-lg, 0 24px 60px rgba(15,23,42,0.18))",
          position: "relative",
        }}
      >
        <button
          data-testid="payment-gateway-close"
          onClick={onClose}
          aria-label="Cerrar"
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: "var(--pl-text-muted, #64748b)",
            padding: 4,
            display: "flex",
          }}
        >
          <CloseIcon className="w-4 h-4" />
        </button>

        <h2
          style={{
            margin: 0,
            marginBottom: 6,
            fontSize: 20,
            fontWeight: 700,
            letterSpacing: "-0.015em",
            color: "var(--pl-text-strong, #0f172a)",
          }}
        >
          Elige cómo pagar
        </h2>
        <p
          style={{
            margin: 0,
            marginBottom: 20,
            fontSize: 13,
            color: "var(--pl-text-muted, #64748b)",
            lineHeight: 1.5,
          }}
        >
          {planLabel}. Se renueva automáticamente cada mes — cancelas cuando quieras.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <GatewayButton
            testId="pay-with-flow"
            logo={<FlowLogo />}
            label="Flow.cl"
            sublabel="Webpay, tarjetas chilenas, transferencia"
            badge="Recomendado en Chile"
            disabled={disabled}
            loading={loading === "flow"}
            onClick={() => onSelect("flow")}
            color="#0066ff"
          />
          <GatewayButton
            testId="pay-with-mp"
            logo={<MercadoPagoLogo />}
            label="Mercado Pago"
            sublabel="Tarjeta de crédito o débito"
            disabled={disabled}
            loading={loading === "mp"}
            onClick={() => onSelect("mp")}
            color="#00b1ea"
          />

          {showForeignHint ? (
            <div
              data-testid="payment-gateway-foreign-hint"
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
                padding: "10px 12px",
                borderRadius: 10,
                background: "rgba(0, 177, 234, 0.08)",
                border: "1px solid rgba(0, 177, 234, 0.25)",
                marginTop: 4,
              }}
            >
              <Globe2 className="w-4 h-4" style={{ color: "#0084c1", flexShrink: 0, marginTop: 2 }} />
              <p
                style={{
                  margin: 0,
                  fontSize: 12,
                  lineHeight: 1.45,
                  color: "var(--pl-text, #0f172a)",
                }}
              >
                <strong style={{ color: "#0084c1" }}>Mercado Pago</strong> acepta tarjetas
                extranjeras (Visa / Mastercard internacional). Si estás fuera de Chile, esta es
                tu mejor opción.
              </p>
            </div>
          ) : null}
        </div>

        {error ? (
          <p
            data-testid="payment-gateway-error"
            style={{
              margin: "16px 0 0 0",
              fontSize: 13,
              color: "#b91c1c",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              padding: "8px 12px",
              borderRadius: 8,
            }}
          >
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function GatewayButton({ testId, logo, label, sublabel, badge, disabled, loading, onClick, color }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 14,
        padding: "12px 14px",
        borderRadius: 12,
        background: "var(--pl-card-soft, #f8fafc)",
        border: `1.5px solid ${color}33`,
        color: "var(--pl-text, #0f172a)",
        cursor: disabled ? "wait" : "pointer",
        textAlign: "left",
        opacity: disabled && !loading ? 0.55 : 1,
        transition: "all 0.15s ease",
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = `${color}0d`;
          e.currentTarget.style.borderColor = `${color}66`;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "var(--pl-card-soft, #f8fafc)";
        e.currentTarget.style.borderColor = `${color}33`;
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
        <div
          aria-hidden="true"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {logo}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: "var(--pl-text-strong, #0f172a)" }}>
              {label}
            </span>
            {badge ? (
              <span
                style={{
                  fontSize: 10,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: color,
                  color: "#fff",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                }}
              >
                {badge}
              </span>
            ) : null}
          </div>
          <div style={{ fontSize: 12, color: "var(--pl-text-muted, #64748b)", marginTop: 2 }}>
            {sublabel}
          </div>
        </div>
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color,
          display: "flex",
          alignItems: "center",
          gap: 6,
          flexShrink: 0,
        }}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continuar →"}
      </div>
    </button>
  );
}
